import React, { useState, useRef, MouseEvent } from "react";
import { ShieldCheck, Cpu, Timer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Insurance Ease",
  icon: ShieldCheck,
  description: "We specialize in seamless insurance claim management for hail damage and structural repairs, working directly with major providers in Sydney."
}, {
  title: "Premium Materials",
  icon: Cpu,
  description: "From high-grade aluminum to marine-spec electrical components, we utilize only industrial-strength materials built for the Australian outback."
}, {
  title: "Quality Standards",
  icon: Timer,
  description: "Our uncompromising multi-point inspection covers plumbing, electrical, and structural integrity to ensure every caravan is road-ready."
}];
export default function ThePhoneFixerDifference() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="42500" id="the-caravan-smash-repairs-standards" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-primary mb-6">
            The Caravan Repair Standard
          </h2>
          <p className="text-lg text-muted-foreground font-medium leading-relaxed">
            Combining industrial precision with decades of craftsmanship in South Windsor. 
            We restore more than just bodywork; we ensure your freedom to explore remains uninterrupted.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {features.map((feature, i) => <SpotlightCard key={i} feature={feature} index={i} />)}
        </div>
      </div>
    </section>;
}
function SpotlightCard({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [opacity, setOpacity] = useState(0);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const handleMouseEnter = () => {
    setOpacity(1);
  };
  const handleMouseLeave = () => {
    setOpacity(0);
  };
  const Icon = feature.icon;
  return <div data-index={index} ref={divRef} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="group relative rounded-xl border border-border bg-card/50 backdrop-blur-md overflow-hidden transition-all hover:border-secondary/40 hover:shadow-xl">
      {}
      <div className="pointer-events-none absolute -inset-px transition-opacity duration-300" style={{
      opacity,
      background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, var(--color-secondary), transparent 40%)`,
      mixBlendMode: "soft-light"
    }} />
      
      {}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/circuit_pattern_texture.png')] bg-cover bg-center mix-blend-multiply" style={{
      maskImage: `radial-gradient(400px circle at ${position.x}px ${position.y}px, black, transparent)`,
      WebkitMaskImage: `radial-gradient(400px circle at ${position.x}px ${position.y}px, black, transparent)`
    }} />

      <Card className="bg-transparent border-none shadow-none h-full relative z-10">
        <CardHeader className="pb-4">
          <div className="mb-6 inline-flex items-center justify-center size-16 rounded-xl bg-secondary/10 border border-secondary/20 text-secondary shadow-[0_0_20px_-5px_var(--color-secondary)] group-hover:scale-110 transition-transform duration-500">
            <Icon className="size-8" />
          </div>
          <CardTitle className="text-2xl font-bold text-primary group-hover:text-secondary transition-colors font-sans tracking-tight">
            {feature.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base text-muted-foreground leading-relaxed font-medium">
            {feature.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}